<template>
  <v-card variant="flat" class="rounded-xl gm-card" title="运行示意图">
    <div class="diagram-content ma-4 mt-0">
      <div class="diagram-viewport">
        <div class="diagram-stage">
          <div class="device device-pv" :class="{ 'is-optional-off': !hasPvModule }">
            <img src="./img/diagram/pv.png" alt="光伏" class="device-icon icon-pv" />
            <div class="device-name">
              <span class="status-dot"></span>
              光伏
            </div>
          </div>

          <div class="device device-grid">
            <img src="./img/diagram/grid.png" alt="电网" class="device-icon icon-grid" />
            <div class="device-name">电网</div>
          </div>

          <div class="device device-ess">
            <img src="./img/diagram/eb.png" alt="储能系统" class="device-icon icon-ess" />
            <div class="device-name">储能系统</div>
          </div>

          <div class="device device-charge" :class="{ 'is-optional-off': !hasChargerModule }">
            <img src="./img/diagram/charge-pile.png" alt="充电桩" class="device-icon icon-charge" />
            <div class="device-name">充电桩</div>
          </div>

          <div class="device device-load">
            <img src="./img/diagram/load.png" alt="负载" class="device-icon icon-load" />
            <div class="device-name">负载</div>
          </div>

          <svg class="line-pv-svg" :class="{ 'is-optional-off': !hasPvModule }" xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true">
            <!-- 对齐原 line-pv-main / line-pv-active：竖线 x=70 -->
            <path class="line-pv-path" d="M 70 110 L 70 160" />
            <!-- <path class="line-pv-path-active" d="M 70 121 L 70 161" /> -->
          </svg>
          <svg class="line-bus-svg" :class="{ 'is-optional-off': !hasPvModule }" xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true">
            <!-- 光伏 -> 储能系统：横线 y=160 -->
            <path class="line-bus-path" d="M 70 160 L 238 160" />
            <!-- <path class="line-bus-path-active" d="M 70 160 L 238 160" /> -->
          </svg>
          <svg class="line-charge-svg" :class="{ 'is-optional-off': !hasChargerModule }"
            xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <!-- 对齐原 line-charge-main / line-charge-active：竖线 x=70 -->
            <path class="line-charge-path" d="M 70 161 L 70 241" />
            <!-- <path class="line-charge-path-active" d="M 70 161 L 70 241" /> -->
          </svg>
          <svg class="line-grid-svg" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <!-- 对齐原 line-grid-h / line-grid-v：横线 y=74，竖线 x=260 -->
            <path class="line-grid-path" d="M 260 74 L 352 74 L 260 74 L 260 116" />
            <!-- 储能系统 -> 电网：先上再右 -->
            <path class="line-grid-path-active" d="M 260 116 L 260 74 L 352 74" v-if="gridPower != 0"
              :class="{ 'reverse': gridPower > 0 }" />
          </svg>
          <svg class="line-load-svg" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <!-- 与原先 line-load-v / line-load-h / line-load-active 对齐：竖线 x=261，横线 y=307 -->
            <path class="line-load-path" d="M 261 266 L 261 286 L 261 308 L 386 307" />
            <!-- 储能系统 -> 负载：先下再右 -->
            <path class="line-load-path-active" d="M 261 266 L 261 308 L 386 307" v-if="loadPower != 0"
              :class="{ 'reverse': loadPower < 0 }" />
          </svg>

          <div class="data-tag tag-pv" :class="{ 'is-optional-off': !hasPvModule }" v-if="pvPower != '--'">
            <div>P: {{ pvPower }}kW</div>
            <div>U: {{ pvVoltage }}V</div>
            <div>I: {{ pvCurrent }}A</div>
          </div>

          <div class="data-tag tag-grid" v-if="gridPower != '--'">
            <div>P: {{ gridPower }}kW</div>
            <div>U: {{ gridVoltage }}V</div>
            <div>I: {{ gridCurrent }}A</div>
          </div>

          <div class="data-tag tag-ess" v-if="essPower != '--'">
            <div>P: {{ essPower }}kW</div>
            <div>U: {{ essVoltage }}V</div>
            <div>I: {{ essCurrent }}A</div>
          </div>

          <div class="data-tag tag-load" v-if="loadPower != '--'">
            <div>P: {{ loadPower }}kW</div>
            <div>U: {{ loadVoltage }}V</div>
            <div>I: {{ loadCurrent }}A</div>
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>




<script setup>
import { computed } from 'vue'

const props = defineProps({
  hasPvModule: {
    type: Boolean,
    default: false
  },
  hasChargerModule: {
    type: Boolean,
    default: false
  },
  diagramData: {
    type: Object,
    default: () => ({})
  }
})

const riagramData = computed(() => props.diagramData?.riagram || {})

const pvData = computed(() => riagramData.value?.pv || {})
const gridData = computed(() => riagramData.value?.grid || {})
const essData = computed(() => riagramData.value?.ess || {})
const loadData = computed(() => riagramData.value?.load || {})

const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '--'
  return value
}

const pickValue = (data, keys) => {
  for (const key of keys) {
    const value = data?.[key]
    if (value !== null && value !== undefined && value !== '') return value
  }
  return '--'
}

const pvPower = computed(() => displayValue(pickValue(pvData.value, ['p', 'power', 'p_total'])))
const pvVoltage = computed(() => displayValue(pickValue(pvData.value, ['u', 'u_ab', 'u_a'])))
const pvCurrent = computed(() => displayValue(pickValue(pvData.value, ['i', 'i_a'])))

const gridPower = computed(() => displayValue(pickValue(gridData.value, ['p', 'power', 'p_total'])))
const gridCurrent = computed(() => displayValue(gridData.value?.i_a))
const gridVoltage = computed(() => displayValue(gridData.value?.u_ab))

const essPower = computed(() => displayValue(pickValue(essData.value, ['power'])))
const essVoltage = computed(() => displayValue(pickValue(essData.value, ['voltage'])))
const essCurrent = computed(() => displayValue(pickValue(essData.value, ['current'])))


const loadPower = computed(() => displayValue(pickValue(loadData.value, ['p', 'power', 'p_total'])))
const loadCurrent = computed(() => displayValue(pickValue(loadData.value, ['i', 'i_a'])))
const loadVoltage = computed(() => displayValue(pickValue(loadData.value, ['u', 'u_ab', 'u_a'])))
</script>




<style scoped lang="scss">
.diagram-content {
  container-type: inline-size;
  height: clamp(296px, calc(100cqw * 360 / 520), 360px);
}

.diagram-viewport {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.diagram-stage {
  position: relative;
  width: 520px;
  height: 360px;
  transform-origin: left top;
  border-radius: 12px;
  overflow: hidden;
  background-color: #f1f4fd38;
  // border: 1px solid #dde5f1;
  background-image:
    repeating-linear-gradient(-28deg, rgba(125, 148, 185, 0.08) 0 1px, transparent 1px 50px),
    repeating-linear-gradient(28deg, rgba(125, 148, 185, 0.06) 0 1px, transparent 1px 50px);
}

@container (max-width: 520px) {
  .diagram-stage {
    transform: scale(calc(100cqw / 520));
  }
}

.device {
  position: absolute;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.device-icon {
  object-fit: contain;
  user-select: none;
  pointer-events: none;
}

.device-name {
  margin-top: 8px;
  font-size: 32px;
  line-height: 1;
  color: #5f6773;
  transform: scale(0.5);
  transform-origin: center top;
  white-space: nowrap;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #20d07a;
  display: inline-block;
  margin-right: 8px;
  transform: translateY(-1px);
}

.device-pv {
  top: 22px;
  left: 30px;
}

.icon-pv {
  width: 80px;
}

.device-grid {
  top: 12px;
  right: 110px;
}

.icon-grid {
  width: 82px;
  height: 110px;
}

.device-ess {
  top: 108px;
  left: 200px;
}

.icon-ess {
  width: 88px;
  height: 104px;
}

.device-charge {
  left: 28px;
  bottom: 0px;
}

.icon-charge {
  width: 68px;
  height: 92px;
}

.device-load {
  bottom: -10px;
  right: 116px;
}

.icon-load {
  width: 80px;
  // height: 80px;
}

.line {
  position: absolute;
  z-index: 2;
  background: #d4e0f3;
}

.line-pv-svg {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.line-pv-path {
  fill: none;
  stroke: #d4e0f3;
  stroke-width: 2;
  stroke-linecap: square;
}

.line-pv-path-active {
  fill: none;
  stroke: #276cff;
  stroke-width: 2;
  stroke-linecap: square;
}

.line-bus-svg {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.line-bus-path {
  fill: none;
  stroke: #d4e0f3;
  stroke-width: 2;
  stroke-linecap: square;
}

.line-bus-path-active {
  fill: none;
  stroke: #276cff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 8 10;
  animation: line-bus-flow 1.2s linear infinite reverse;
}

@keyframes line-bus-flow {
  from {
    stroke-dashoffset: 0;
  }

  to {
    stroke-dashoffset: -18;
  }
}

.line-charge-svg {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.line-charge-path {
  fill: none;
  stroke: #d4e0f3;
  stroke-width: 2;
  stroke-linecap: square;
}

.line-charge-path-active {
  fill: none;
  stroke: #276cff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 8 10;
  animation: line-charge-flow 1.2s linear infinite;
}

@keyframes line-charge-flow {
  from {
    stroke-dashoffset: 0;
  }

  to {
    stroke-dashoffset: -18;
  }
}

.line-grid-svg {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.line-grid-path {
  fill: none;
  stroke: #d4e0f3;
  stroke-width: 2;
  stroke-linecap: square;
}

.line-grid-path-active {
  fill: none;
  stroke: #276cff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 8 10;
  animation: line-grid-flow 1.2s linear infinite;
}

.reverse {
  animation-direction: reverse !important;
}

@keyframes line-grid-flow {
  from {
    stroke-dashoffset: 0;
  }

  to {
    stroke-dashoffset: -18;
  }
}

.line-load-svg {
  position: absolute;
  z-index: 2;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.line-load-path {
  fill: none;
  stroke: #d4e0f3;
  stroke-width: 2;
  stroke-linecap: square;
}

.line-load-path-active {
  fill: none;
  stroke: #276cff;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 8 10;
  animation: line-load-flow 1.2s linear infinite;
}

@keyframes line-load-flow {
  from {
    stroke-dashoffset: 0;
  }

  to {
    stroke-dashoffset: -18;
  }
}

.data-tag {
  position: absolute;
  z-index: 4;
  color: #2f2f33;
  font-size: 14px;
  line-height: 1.55;
  white-space: nowrap;
}

.tag-pv {
  top: 30px;
  left: 138px;
}

.tag-grid {
  top: 46px;
  /* 舞台宽 520px 且 overflow: hidden，原来的 left: 430px 只剩 90px 可用，
     "P: 1826.678kW" 约 115px，尾部会被硬裁。
     改为贴右边界、向左生长，标签变长时不再撞边界 */
  right: 4px;
  text-align: right;
}

.tag-ess {
  top: 158px;
  left: 322px;
}

.tag-load {
  top: 266px;
  right: 4px;
  text-align: right;
}

.is-optional-off {
  opacity: 0.3;
  // 对比度降低
  //  filter: grayscale(100%);
}
</style>
